import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { type ListResponse } from '@zag/core/models';
import { type PreferenceResponse, type NotificationResponse, type DeliveryResponse } from '@zag/notifications/common';
import { NotificationGatewayClient } from '../gateways/notification-gateway.client';
import { Observable } from 'rxjs';

export interface NotificationFilters {
  take?: number;
  skip?: number;
  type?: string;
  category?: string;
  read?: boolean;
  priority?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  readonly gateway = inject(NotificationGatewayClient);

  // Real-time unread count from WebSocket
  readonly unreadCount = this.gateway.unreadCount;
  readonly hasUnread = this.gateway.hasUnread;

  // Signal-based filter state driving reactive list
  readonly listFilters = signal<NotificationFilters>({ take: 50, skip: 0 });

  // Reactive notification list (re-fetches whenever listFilters changes)
  readonly listResource = rxResource({
    params: () => this.listFilters(),
    stream: ({ params }) => {
      const queryParams: Record<string, string> = {};
      if (params.take !== undefined) queryParams['take'] = String(params.take);
      if (params.skip !== undefined) queryParams['skip'] = String(params.skip);
      if (params.type !== undefined) queryParams['type'] = params.type;
      if (params.category !== undefined) queryParams['category'] = params.category;
      if (params.read !== undefined) queryParams['read'] = String(params.read);
      if (params.priority !== undefined) queryParams['priority'] = params.priority;
      return this.http.get<ListResponse<NotificationResponse>>('/api/v1/notifications', { params: queryParams });
    },
  });

  getNotification(id: string): Observable<NotificationResponse> {
    return this.http.get<NotificationResponse>(`/api/v1/notifications/${id}`);
  }

  getDeliveries(notificationId: string): Observable<DeliveryResponse[]> {
    return this.http.get<DeliveryResponse[]>(`/api/v1/notifications/${notificationId}/deliveries`);
  }

  markAsRead(id: string): Observable<NotificationResponse> {
    this.gateway.markAsRead(id);
    return this.http.put<NotificationResponse>(`/api/v1/notifications/${id}/read-receipt`, {});
  }

  markAllAsRead(): Observable<{ updated: number }> {
    this.gateway.markAllAsRead();
    return this.http.post<{ updated: number }>('/api/v1/notifications/read-receipts', {});
  }

  archive(id: string): Observable<NotificationResponse> {
    return this.http.put<NotificationResponse>(`/api/v1/notifications/${id}/archive-state`, { archived: true });
  }

  resend(id: string): Observable<{ notification: NotificationResponse; newDeliveryIds: string[] }> {
    return this.http.post<{ notification: NotificationResponse; newDeliveryIds: string[] }>(`/api/v1/notifications/${id}/resends`, {});
  }

  retryDelivery(deliveryId: string): Observable<DeliveryResponse> {
    return this.http.post<DeliveryResponse>(`/api/v1/notifications/deliveries/${deliveryId}/retry`, {});
  }

  getPreferences(): Observable<PreferenceResponse[]> {
    return this.http.get<PreferenceResponse[]>('/api/v1/notifications/preferences');
  }

  updatePreferences(preferences: { event_type: string; channel: string; enabled: boolean }[]): Observable<PreferenceResponse[]> {
    return this.http.put<PreferenceResponse[]>('/api/v1/notifications/preferences', { preferences });
  }
}
